import { useEffect, useState, useMemo, FormEvent } from "react";
import axios from "axios";
import { NewSideBarAdmin } from "../../NewSideBarAdmin";

interface EnCompteRelation {
  id: number;
  magasin_fournisseur: number;
  magasin_client: number;
  NomMagasinFournisseur: string;
  NomMagasinClient: string;
  date: string;
  solde: number | string;
  created_at?: string;
  updated_at?: string;
}

export const AdminEnCompteTracking = () => {
  const [relations, setRelations] = useState<EnCompteRelation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [filterSolde, setFilterSolde] = useState<"all" | "positive" | "zero">("all");
  const [sortField, setSortField] = useState<"date" | "solde" | "fournisseur" | "client">("date");
  const [sortAsc, setSortAsc] = useState<boolean>(false);

  // Edit Solde Modal
  const [editingItem, setEditingItem] = useState<EnCompteRelation | null>(null);
  const [newSolde, setNewSolde] = useState<string>("");
  const [editLoading, setEditLoading] = useState<boolean>(false);

  // Delete Modal
  const [deletingItem, setDeletingItem] = useState<EnCompteRelation | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<boolean>(false);

  // Toast / notification feedback
  const [toastMessage, setToastMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  const showToast = (text: string, type: "success" | "error" = "success") => {
    setToastMessage({ text, type });
    setTimeout(() => setToastMessage(null), 3500);
  };

  const fetchRelations = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_PHP_APP_URL}/admin/en-compte`);
      setRelations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Erreur lors de la récupération des magasins en compte:", err);
      showToast("Impossible de charger les magasins en compte", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRelations();
  }, []);

  const handleUpdateSolde = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;
    setEditLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/en-compte/${editingItem.id}/update-solde`,
        { solde: parseFloat(newSolde) || 0 }
      );
      setRelations((prev) =>
        prev.map((r) =>
          r.id === editingItem.id ? { ...r, solde: parseFloat(newSolde) || 0 } : r
        )
      );
      showToast("Solde mis à jour avec succès");
      setEditingItem(null);
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la mise à jour du solde", "error");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    setDeleteLoading(true);
    try {
      await axios.delete(
        `${process.env.REACT_APP_PHP_APP_URL}/admin/en-compte/${deletingItem.id}`
      );
      setRelations((prev) => prev.filter((r) => r.id !== deletingItem.id));
      showToast("Relation en-compte supprimée");
      setDeletingItem(null);
    } catch (err) {
      console.error(err);
      showToast("Erreur lors de la suppression de la relation", "error");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Metrics
  const metrics = useMemo(() => {
    const totalRelations = relations.length;
    const totalSolde = relations.reduce(
      (acc, r) => acc + (parseFloat(String(r.solde)) || 0),
      0
    );
    const uniqueFournisseurs = new Set(relations.map((r) => r.magasin_fournisseur)).size;
    const uniqueClients = new Set(relations.map((r) => r.magasin_client)).size;
    const relationsWithDebt = relations.filter(
      (r) => (parseFloat(String(r.solde)) || 0) > 0
    ).length;

    return {
      totalRelations,
      totalSolde,
      uniqueFournisseurs,
      uniqueClients,
      relationsWithDebt,
    };
  }, [relations]);

  // Filtering and Sorting
  const filteredRelations = useMemo(() => {
    return relations
      .filter((r) => {
        const query = search.trim().toLowerCase();
        const matchesSearch =
          !query ||
          (r.NomMagasinFournisseur && r.NomMagasinFournisseur.toLowerCase().includes(query)) ||
          (r.NomMagasinClient && r.NomMagasinClient.toLowerCase().includes(query)) ||
          String(r.id).includes(query);

        const valSolde = parseFloat(String(r.solde)) || 0;
        let matchesSolde = true;
        if (filterSolde === "positive") matchesSolde = valSolde > 0;
        if (filterSolde === "zero") matchesSolde = valSolde === 0;

        return matchesSearch && matchesSolde;
      })
      .sort((a, b) => {
        let valA: any = "";
        let valB: any = "";
        if (sortField === "date") {
          valA = new Date(a.date || a.created_at || "").getTime();
          valB = new Date(b.date || b.created_at || "").getTime();
        } else if (sortField === "solde") {
          valA = parseFloat(String(a.solde)) || 0;
          valB = parseFloat(String(b.solde)) || 0;
        } else if (sortField === "fournisseur") {
          valA = (a.NomMagasinFournisseur || "").toLowerCase();
          valB = (b.NomMagasinFournisseur || "").toLowerCase();
        } else if (sortField === "client") {
          valA = (a.NomMagasinClient || "").toLowerCase();
          valB = (b.NomMagasinClient || "").toLowerCase();
        }

        if (valA < valB) return sortAsc ? -1 : 1;
        if (valA > valB) return sortAsc ? 1 : -1;
        return 0;
      });
  }, [relations, search, filterSolde, sortField, sortAsc]);

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "#f8fafc" }}>
      <NewSideBarAdmin />
      <div
        style={{
          flex: 1,
          padding: "28px 32px",
          minWidth: 0,
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Toast */}
        {toastMessage && (
          <div
            style={{
              position: "fixed",
              top: "24px",
              right: "24px",
              zIndex: 9999,
              padding: "14px 22px",
              borderRadius: "12px",
              background: toastMessage.type === "success" ? "#10b981" : "#ef4444",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.9rem",
              boxShadow: "0 10px 25px -5px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              animation: "fadeIn 0.2s ease",
            }}
          >
            <span>{toastMessage.text}</span>
          </div>
        )}

        {/* Page Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "28px",
          }}
        >
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px" }}>
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, #FFB800 0%, #D97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)",
                }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                  <line x1="6" y1="15" x2="10" y2="15" />
                </svg>
              </div>
              <h1 style={{ margin: 0, fontSize: "1.65rem", fontWeight: 800, color: "#0f172a" }}>
                Suivi Magasins En Compte
              </h1>
            </div>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
              Supervisez les crédits inter-magasins, les soldes engagés et les relations fournisseurs/clients.
            </p>
          </div>

          <button
            onClick={fetchRelations}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "#fff",
              border: "1.5px solid #e2e8f0",
              color: "#334155",
              padding: "10px 18px",
              borderRadius: "12px",
              fontSize: "0.875rem",
              fontWeight: 700,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              transition: "all 0.15s ease",
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 4v6h-6" />
              <path d="M1 20v-6h6" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
            Actualiser
          </button>
        </div>

        {/* Metrics Cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
            gap: "18px",
            marginBottom: "28px",
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px 22px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
              Total Relations
            </span>
            <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#0f172a", marginTop: "6px" }}>
              {metrics.totalRelations}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#10b981", fontWeight: 600, marginTop: "4px" }}>
              {metrics.relationsWithDebt} avec solde actif
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: "20px 22px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
              Total Solde Engagé
            </span>
            <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#b45309", marginTop: "6px" }}>
              {metrics.totalSolde.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
            </div>
            <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "4px" }}>
              Dette globale entre magasins
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: "20px 22px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
              Fournisseurs (Créanciers)
            </span>
            <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#1e40af", marginTop: "6px" }}>
              {metrics.uniqueFournisseurs}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "4px" }}>
              Magasins accordant du crédit
            </div>
          </div>

          <div
            style={{
              background: "#fff",
              padding: "20px 22px",
              borderRadius: "16px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
            }}
          >
            <span style={{ fontSize: "0.8rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>
              Clients (Débiteurs)
            </span>
            <div style={{ fontSize: "1.9rem", fontWeight: 800, color: "#7c3aed", marginTop: "6px" }}>
              {metrics.uniqueClients}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#64748b", marginTop: "4px" }}>
              Magasins sous contrat en compte
            </div>
          </div>
        </div>

        {/* Filters and Search Bar */}
        <div
          style={{
            background: "#fff",
            padding: "18px 22px",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            marginBottom: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "14px",
          }}
        >
          {/* Search */}
          <div style={{ position: "relative", flex: 1, minWidth: "260px" }}>
            <svg
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "#94a3b8",
                pointerEvents: "none",
              }}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher par nom de fournisseur ou de client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: "100%",
                padding: "10px 14px 10px 42px",
                borderRadius: "10px",
                border: "1.5px solid #e2e8f0",
                fontSize: "0.875rem",
                color: "#0f172a",
                outline: "none",
                background: "#f8fafc",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Solde Filter */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              onClick={() => setFilterSolde("all")}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "0.825rem",
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                background: filterSolde === "all" ? "#0f172a" : "#f1f5f9",
                color: filterSolde === "all" ? "#fff" : "#475569",
              }}
            >
              Tous ({relations.length})
            </button>
            <button
              onClick={() => setFilterSolde("positive")}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "0.825rem",
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                background: filterSolde === "positive" ? "#b45309" : "#f1f5f9",
                color: filterSolde === "positive" ? "#fff" : "#475569",
              }}
            >
              Solde &gt; 0 ({relations.filter((r) => (parseFloat(String(r.solde)) || 0) > 0).length})
            </button>
            <button
              onClick={() => setFilterSolde("zero")}
              style={{
                padding: "8px 14px",
                borderRadius: "10px",
                fontSize: "0.825rem",
                fontWeight: 700,
                border: "none",
                cursor: "pointer",
                background: filterSolde === "zero" ? "#10b981" : "#f1f5f9",
                color: filterSolde === "zero" ? "#fff" : "#475569",
              }}
            >
              Solde = 0 ({relations.filter((r) => (parseFloat(String(r.solde)) || 0) === 0).length})
            </button>
          </div>
        </div>

        {/* Main Content Table */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
          }}
        >
          {loading ? (
            <div style={{ padding: "4rem", textAlign: "center", color: "#64748b" }}>
              <div
                style={{
                  display: "inline-block",
                  width: "36px",
                  height: "36px",
                  border: "3px solid #e2e8f0",
                  borderTopColor: "#FFB800",
                  borderRadius: "50%",
                  animation: "spin 0.8s linear infinite",
                  marginBottom: "12px",
                }}
              />
              <p style={{ margin: 0, fontWeight: 600 }}>Chargement des données en compte...</p>
            </div>
          ) : filteredRelations.length === 0 ? (
            <div style={{ padding: "4rem 2rem", textAlign: "center", color: "#64748b" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "14px",
                  background: "#fef3c7",
                  color: "#d97706",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 16px",
                }}
              >
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <h3 style={{ margin: "0 0 6px", color: "#1e293b", fontWeight: 700 }}>
                Aucune relation en compte trouvée
              </h3>
              <p style={{ margin: 0, fontSize: "0.875rem", color: "#64748b" }}>
                {search ? "Modifiez vos filtres de recherche" : "Aucun magasin n'est configuré en compte pour le moment."}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1.5px solid #e2e8f0" }}>
                    <th style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
                      ID
                    </th>
                    <th
                      onClick={() => {
                        if (sortField === "fournisseur") setSortAsc(!sortAsc);
                        else {
                          setSortField("fournisseur");
                          setSortAsc(true);
                        }
                      }}
                      style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", cursor: "pointer" }}
                    >
                      Fournisseur (Créancier) {sortField === "fournisseur" && (sortAsc ? "▲" : "▼")}
                    </th>
                    <th
                      onClick={() => {
                        if (sortField === "client") setSortAsc(!sortAsc);
                        else {
                          setSortField("client");
                          setSortAsc(true);
                        }
                      }}
                      style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", cursor: "pointer" }}
                    >
                      Client (Débiteur) {sortField === "client" && (sortAsc ? "▲" : "▼")}
                    </th>
                    <th
                      onClick={() => {
                        if (sortField === "solde") setSortAsc(!sortAsc);
                        else {
                          setSortField("solde");
                          setSortAsc(false);
                        }
                      }}
                      style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", cursor: "pointer" }}
                    >
                      Solde Actuel {sortField === "solde" && (sortAsc ? "▲" : "▼")}
                    </th>
                    <th
                      onClick={() => {
                        if (sortField === "date") setSortAsc(!sortAsc);
                        else {
                          setSortField("date");
                          setSortAsc(false);
                        }
                      }}
                      style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", cursor: "pointer" }}
                    >
                      Date Début {sortField === "date" && (sortAsc ? "▲" : "▼")}
                    </th>
                    <th style={{ padding: "14px 20px", fontSize: "0.75rem", fontWeight: 700, color: "#64748b", textTransform: "uppercase", textAlign: "right" }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRelations.map((rel) => {
                    const soldeVal = parseFloat(String(rel.solde)) || 0;
                    return (
                      <tr
                        key={rel.id}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          transition: "background 0.15s ease",
                        }}
                      >
                        <td style={{ padding: "16px 20px", fontSize: "0.85rem", fontWeight: 700, color: "#94a3b8" }}>
                          #{rel.id}
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                                background: "#eff6ff",
                                color: "#1d4ed8",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                              }}
                            >
                              {(rel.NomMagasinFournisseur || "F").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>
                                {rel.NomMagasinFournisseur}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                Magasin #{rel.magasin_fournisseur}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                            <div
                              style={{
                                width: "34px",
                                height: "34px",
                                borderRadius: "8px",
                                background: "#f5f3ff",
                                color: "#7c3aed",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 700,
                                fontSize: "0.85rem",
                              }}
                            >
                              {(rel.NomMagasinClient || "C").charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.9rem" }}>
                                {rel.NomMagasinClient}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                                Magasin #{rel.magasin_client}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: "16px 20px" }}>
                          <span
                            style={{
                              display: "inline-block",
                              padding: "6px 12px",
                              borderRadius: "8px",
                              fontWeight: 800,
                              fontSize: "0.9rem",
                              background: soldeVal > 0 ? "#fef3c7" : "#ecfdf5",
                              color: soldeVal > 0 ? "#92400e" : "#065f46",
                              border: soldeVal > 0 ? "1px solid #fde68a" : "1px solid #a7f3d0",
                            }}
                          >
                            {soldeVal.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD
                          </span>
                        </td>
                        <td style={{ padding: "16px 20px", fontSize: "0.85rem", color: "#475569", fontWeight: 500 }}>
                          {rel.date ? new Date(rel.date).toLocaleDateString("fr-FR") : "-"}
                        </td>
                        <td style={{ padding: "16px 20px", textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
                            <button
                              onClick={() => {
                                setEditingItem(rel);
                                setNewSolde(String(rel.solde));
                              }}
                              title="Modifier le solde"
                              style={{
                                background: "#f8fafc",
                                border: "1px solid #cbd5e1",
                                color: "#0284c7",
                                padding: "6px 12px",
                                borderRadius: "8px",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M12 20h9" />
                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                              </svg>
                              Ajuster
                            </button>
                            <button
                              onClick={() => setDeletingItem(rel)}
                              title="Supprimer la relation"
                              style={{
                                background: "#fff1f2",
                                border: "1px solid #fecdd3",
                                color: "#e11d48",
                                padding: "6px 10px",
                                borderRadius: "8px",
                                fontSize: "0.8rem",
                                fontWeight: 700,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Modifier Solde */}
        {editingItem && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.6)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "16px",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "18px",
                padding: "26px",
                maxWidth: "460px",
                width: "100%",
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ margin: "0 0 8px", fontSize: "1.25rem", color: "#0f172a" }}>
                Ajuster le solde en compte
              </h3>
              <p style={{ margin: "0 0 20px", fontSize: "0.85rem", color: "#64748b" }}>
                Relation : <strong>{editingItem.NomMagasinFournisseur}</strong> (fournisseur) →{" "}
                <strong>{editingItem.NomMagasinClient}</strong> (client)
              </p>

              <form onSubmit={handleUpdateSolde}>
                <div style={{ marginBottom: "20px" }}>
                  <label style={{ display: "block", fontSize: "0.8rem", fontWeight: 700, color: "#334155", marginBottom: "6px" }}>
                    Nouveau Solde (MAD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newSolde}
                    onChange={(e) => setNewSolde(e.target.value)}
                    required
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: "10px",
                      border: "1.5px solid #cbd5e1",
                      fontSize: "1rem",
                      fontWeight: 700,
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                  <small style={{ color: "#94a3b8", display: "block", marginTop: "5px" }}>
                    Ce solde représente le crédit dont dispose le magasin client auprès du fournisseur.
                  </small>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                  <button
                    type="button"
                    onClick={() => setEditingItem(null)}
                    style={{
                      padding: "10px 18px",
                      borderRadius: "10px",
                      border: "1px solid #cbd5e1",
                      background: "#fff",
                      color: "#475569",
                      fontWeight: 600,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={editLoading}
                    style={{
                      padding: "10px 20px",
                      borderRadius: "10px",
                      border: "none",
                      background: "#FFB800",
                      color: "#0f172a",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(255, 184, 0, 0.4)",
                    }}
                  >
                    {editLoading ? "Enregistrement..." : "Enregistrer"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Modal Delete Confirmation */}
        {deletingItem && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(15, 23, 42, 0.6)",
              backdropFilter: "blur(4px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "16px",
            }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "18px",
                padding: "26px",
                maxWidth: "420px",
                width: "100%",
                boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  borderRadius: "12px",
                  background: "#fff1f2",
                  color: "#e11d48",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "14px",
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <h3 style={{ margin: "0 0 8px", fontSize: "1.2rem", color: "#0f172a" }}>
                Supprimer la relation en compte ?
              </h3>
              <p style={{ margin: "0 0 20px", fontSize: "0.85rem", color: "#64748b" }}>
                Êtes-vous sûr de vouloir supprimer la relation entre{" "}
                <strong>{deletingItem.NomMagasinFournisseur}</strong> et{" "}
                <strong>{deletingItem.NomMagasinClient}</strong> ? Cette action est irréversible.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setDeletingItem(null)}
                  style={{
                    padding: "10px 18px",
                    borderRadius: "10px",
                    border: "1px solid #cbd5e1",
                    background: "#fff",
                    color: "#475569",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                  }}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={deleteLoading}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "10px",
                    border: "none",
                    background: "#e11d48",
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                    cursor: "pointer",
                  }}
                >
                  {deleteLoading ? "Suppression..." : "Confirmer la suppression"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEnCompteTracking;
